import { ClientError, RpcError } from '../client/errors.js';
import { OnlineApiError } from '../online/errors.js';

export const ExitCode = {
  Success: 0,
  General: 1,
  Usage: 2,
  NoDevice: 3,
  Connection: 4,
  Timeout: 5,
  EncryptionRequired: 6,
  Pairing: 7,
  DeviceApi: 8,
  OnlineApi: 9,
  Unsupported: 10,
} as const;

export class CliError extends Error {
  readonly code: number;
  readonly data?: unknown;

  constructor(message: string, code: number = ExitCode.General, data?: unknown) {
    super(message);
    this.name = 'CliError';
    this.code = code;
    this.data = data;
  }
}

export function classifyError(err: unknown): CliError {
  if (err instanceof CliError) return err;
  if (err instanceof RpcError) {
    return new CliError(
      err.message,
      err.kind === 'unsupported' ? ExitCode.Unsupported : ExitCode.DeviceApi,
      {
        kind: err.kind,
        rpc: err.rpc,
      },
    );
  }
  if (err instanceof OnlineApiError) {
    return new CliError(err.message, ExitCode.OnlineApi, {
      kind: err.kind,
      details: err.details,
    });
  }
  if (err instanceof ClientError) {
    const code =
      err.kind === 'timeout'
        ? ExitCode.Timeout
        : err.kind === 'encryption-required' || err.kind === 'pairing-rejected'
          ? ExitCode.EncryptionRequired
          : err.kind === 'unsupported'
            ? ExitCode.Unsupported
            : err.kind === 'device-api'
              ? ExitCode.DeviceApi
              : ExitCode.Connection;
    return new CliError(err.message, code, { kind: err.kind, details: err.details });
  }
  const message = err instanceof Error ? err.message : String(err);
  if (/timed out|timeout/i.test(message)) return new CliError(message, ExitCode.Timeout);
  if (/\bencryption required\b|\bnot paired\b|\bpair\b/i.test(message)) {
    return new CliError(message, ExitCode.EncryptionRequired);
  }
  if (/No device|Unknown device|configured device|discovered/i.test(message)) {
    return new CliError(message, ExitCode.NoDevice);
  }
  if (
    /\bWebSocket\b|\bconnect\b|\bconnection\b|\bclosed\b/i.test(message) ||
    /\b(?:ECONN[A-Z]*|ENOTFOUND|EHOST[A-Z]*)\b/.test(message)
  ) {
    return new CliError(message, ExitCode.Connection);
  }
  return new CliError(message, ExitCode.General);
}
